import type { PropType } from 'virtual-vue';
import type { DatePickerColumnType, DatePickerFormatter, DatePickerFilter } from './types';
import { DEFAULT_COLUMNS_TYPE } from './types';

const currentYear = new Date().getFullYear();

export const DEFAULT_DATE_PICKER_MODEL_VALUE: string[] = [];
export const DEFAULT_DATE_PICKER_VISIBLE = false;
export const DEFAULT_DATE_PICKER_MIN_DATE = new Date(currentYear - 10, 0, 1);
export const DEFAULT_DATE_PICKER_MAX_DATE = new Date(currentYear + 10, 11, 31);
export const DEFAULT_DATE_PICKER_TITLE = '';
export const DEFAULT_DATE_PICKER_CONFIRM_BUTTON_TEXT = '确定';
export const DEFAULT_DATE_PICKER_SHOW_CLOSE = true;
export const DEFAULT_DATE_PICKER_CLOSE_ON_CLICK_OVERLAY = true;
export const DEFAULT_DATE_PICKER_DISABLED = false;
export const DEFAULT_DATE_PICKER_TELEPORT = 'body';
export const DEFAULT_DATE_PICKER_LOCK_SCROLL = true;
export const DEFAULT_DATE_PICKER_DESTROY_ON_CLOSE = false;
export const DEFAULT_DATE_PICKER_VISIBLE_OPTION_NUM = 5;
export const DEFAULT_DATE_PICKER_OPTION_HEIGHT = 112;
export const DEFAULT_DATE_PICKER_SWIPE_DURATION = 150;

/**
 * DatePicker 组件的 props
 * 基于方案设计文档生成
 */
export const props = {
  /**
   * 当前选中的日期值，支持数组格式（如 ['2024', '06', '15']）、字符串格式（如 '2024-06-15'）或 Date 对象（Vue 3 v-model）
   * @default []
   */
  modelValue: {
    type: [Array, String, Date] as PropType<string[] | string | Date>,
    default: () => DEFAULT_DATE_PICKER_MODEL_VALUE,
  },
  /**
   * 当前选中的日期值，支持数组格式（如 ['2024', '06', '15']）、字符串格式（如 '2024-06-15'）或 Date 对象（Vue 2 v-model）
   * @default []
   */
  value: {
    type: [Array, String, Date] as PropType<string[] | string | Date>,
    default: () => DEFAULT_DATE_PICKER_MODEL_VALUE,
  },
  /**
   * 是否显示选择器弹窗
   * @default false
   */
  visible: {
    type: Boolean,
    default: DEFAULT_DATE_PICKER_VISIBLE,
  },
  /**
   * 选项类型，由 year、month、day 组成的数组
   * @default ['year', 'month', 'day']
   */
  columnsType: {
    type: Array as PropType<DatePickerColumnType[]>,
    default: () => [...DEFAULT_COLUMNS_TYPE],
  },
  /**
   * 可选的最小日期
   * @default 当前日期往前10年的1月1日
   */
  minDate: {
    type: Date as unknown as PropType<Date>,
    default: () => DEFAULT_DATE_PICKER_MIN_DATE,
  },
  /**
   * 可选的最大日期
   * @default 当前日期往后10年的12月31日
   */
  maxDate: {
    type: Date as unknown as PropType<Date>,
    default: () => DEFAULT_DATE_PICKER_MAX_DATE,
  },
  /**
   * 弹窗标题
   * @default ''
   */
  title: {
    type: String,
    default: DEFAULT_DATE_PICKER_TITLE,
  },
  /**
   * 确认按钮文字
   * @default '确定'
   */
  confirmButtonText: {
    type: String,
    default: DEFAULT_DATE_PICKER_CONFIRM_BUTTON_TEXT,
  },
  /**
   * 是否显示关闭按钮
   * @default true
   */
  showClose: {
    type: Boolean,
    default: DEFAULT_DATE_PICKER_SHOW_CLOSE,
  },
  /**
   * 点击遮罩层是否关闭
   * @default true
   */
  closeOnClickOverlay: {
    type: Boolean,
    default: DEFAULT_DATE_PICKER_CLOSE_ON_CLICK_OVERLAY,
  },
  /**
   * 是否禁用
   * @default false
   */
  disabled: {
    type: Boolean,
    default: DEFAULT_DATE_PICKER_DISABLED,
  },
  /**
   * v-model 为 string 时的输入输出格式，支持 YYYY、MM、DD 占位符。
   * 同时在 confirm / change 事件参数中输出 formattedValue。
   * @default 'YYYY-MM-DD'
   */
  format: {
    type: String,
    default: 'YYYY-MM-DD',
  },
  /**
   * 选项格式化函数
   */
  optionsFormatter: {
    type: Function as PropType<DatePickerFormatter>,
    default: undefined,
  },
  /**
   * 选项过滤函数
   */
  filter: {
    type: Function as PropType<DatePickerFilter>,
    default: undefined,
  },
  /**
   * 指定挂载节点
   * @default 'body'
   */
  teleport: {
    type: String,
    default: DEFAULT_DATE_PICKER_TELEPORT,
  },
  /**
   * 是否锁定背景滚动
   * @default true
   */
  lockScroll: {
    type: Boolean,
    default: DEFAULT_DATE_PICKER_LOCK_SCROLL,
  },
  /**
   * 关闭时是否销毁
   * @default false
   */
  destroyOnClose: {
    type: Boolean,
    default: DEFAULT_DATE_PICKER_DESTROY_ON_CLOSE,
  },
  /**
   * 可见选项个数
   * @default 5
   */
  visibleOptionNum: {
    type: Number,
    default: DEFAULT_DATE_PICKER_VISIBLE_OPTION_NUM,
  },
  /**
   * 选项行高度（单位与 @unit 对应）
   * @default 112
   */
  optionHeight: {
    type: Number,
    default: DEFAULT_DATE_PICKER_OPTION_HEIGHT,
  },
  /**
   * 惯性滚动时长（ms）
   * @default 150
   */
  swipeDuration: {
    type: Number,
    default: DEFAULT_DATE_PICKER_SWIPE_DURATION,
  },
};
